/**
 * A calendar day with no time of day and no time zone (application date, irrigation period, planting date…).
 *
 * Wire format: `YYYY-MM-DD`. In code, a date-only value is held as a `Date` at **local** midnight of that day:
 * - in the browser, so that date pickers and `formatDateV2` show the same day;
 * - in the API, because the MSSQL driver reads and writes `date` / `datetime2` columns with the process local time.
 *
 * Use `toDateOnly` / `parseDateOnly` for every date-only field, front and back, whatever the time zone. The driver
 * writes a `datetimeoffset` column with the process local time too, but stamps it `+00:00`: a day written with
 * `parseDateOnly` is stored at `T00:00:00+00:00` whatever the API time zone, and read back with `toUtcDateOnly`.
 */
export type DateOnly = string

// `YYYY-MM-DD`, optionally followed by the time and offset of a legacy payload (ISO 8601, or `yyyy-MM-dd HH:mm`)
const DATE_ONLY: RegExp = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

const STRICT_DATE_ONLY: RegExp = /^\d{4}-\d{2}-\d{2}$/

const pad = (value: number, length: number = 2): string => String(value).padStart(length, '0')

const formatDateOnly = (year: number, month: number, day: number): DateOnly =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`

/**
 * Splits a date-only value into its calendar parts. Accepts `YYYY-MM-DD`, and keeps the calendar part of the legacy
 * ISO 8601 payloads (`2026-07-20T00:00:00+02:00`, `2026-07-20T00:00:00.000Z`) whatever their time and offset.
 */
const calendarParts = (value: string): [number, number, number] => {
  const match: RegExpExecArray | null = DATE_ONLY.exec(value)
  if (!match) {
    throw new Error(`Expected a date-only value (YYYY-MM-DD), got "${value}"`)
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const check: Date = new Date(0)
  check.setUTCFullYear(year, month - 1, day)
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    throw new Error(`Expected a date-only value (YYYY-MM-DD), got "${value}"`)
  }
  return [year, month, day]
}

const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.getTime())

/** Whether the value is a valid date-only value or a legacy ISO 8601 payload whose calendar part is valid. */
export function isDateOnly(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }
  try {
    calendarParts(value)
    return true
  } catch {
    return false
  }
}

/** Whether the value is a valid `YYYY-MM-DD` date-only value, legacy ISO 8601 payloads excluded (e.g. a file cell). */
export function isStrictDateOnly(value: unknown): value is DateOnly {
  return typeof value === 'string' && STRICT_DATE_ONLY.test(value) && isDateOnly(value)
}

/**
 * The local calendar day of a date, as `YYYY-MM-DD`.
 *
 * Front: the day picked in a date picker. Back: the day of a `date` / `datetime2` column read by the driver.
 */
export function toDateOnly(date: Date): DateOnly
export function toDateOnly(date: Date | null | undefined): DateOnly | null
export function toDateOnly(date: Date | null | undefined): DateOnly | null {
  if (!date) {
    return null
  }
  if (!isValidDate(date)) {
    throw new Error(`toDateOnly: expected a valid date, got ${date}`)
  }
  return formatDateOnly(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

/**
 * A date-only value as a `Date` at local midnight of that day.
 *
 * Front: the value to give a date picker or to format. Back: the value to write in a `date` / `datetime2` column.
 * Accepts the legacy ISO 8601 payloads and keeps their calendar part.
 */
export function parseDateOnly(value: string): Date
export function parseDateOnly(value: string | null | undefined): Date | null
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) {
    return null
  }
  const [year, month, day] = calendarParts(value)
  // Not `new Date(y, m, d)`, which maps years 0–99 to 1900–1999
  const date: Date = new Date(0)
  date.setFullYear(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * The UTC calendar day of a date, as `YYYY-MM-DD`.
 *
 * Back: the day of a `datetimeoffset` column, which the driver reads back as the instant stamped `+00:00` at write.
 */
export function toUtcDateOnly(date: Date): DateOnly
export function toUtcDateOnly(date: Date | null | undefined): DateOnly | null
export function toUtcDateOnly(date: Date | null | undefined): DateOnly | null {
  if (!date) {
    return null
  }
  if (!isValidDate(date)) {
    throw new Error(`toUtcDateOnly: expected a valid date, got ${date}`)
  }
  return formatDateOnly(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}
